import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Key, Copy, Trash2, Plus, Eye, EyeOff, Check, Shield, Book, AlertTriangle, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

const API_BASE_URL = "https://vdmfopotjhinpmtpopny.supabase.co/functions/v1";

export function ApiKeyManagement() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchApiKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('id, name, key_prefix, created_at, last_used_at, is_active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (error: any) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const generateNewKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setGenerating(true);
    try {
      const { data, error } = await supabase.rpc('generate_api_key', {
        key_name: newKeyName.trim()
      });

      if (error) {
        console.error('RPC Error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from generate_api_key');
      }

      const result = data as { id: string; key: string; prefix: string; message: string };
      setGeneratedKey(result.key);
      setShowKey(true);
      setCopied(false);
      toast.success('API key generated successfully!');
      fetchApiKeys();
      setNewKeyName("");
    } catch (error: any) {
      console.error('Error generating API key:', error);
      toast.error(`Failed to generate API key: ${error.message || 'Unknown error'}`);
    } finally {
      setGenerating(false);
    }
  };

  const deleteKey = async (id: string) => {
    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('API key deleted');
      fetchApiKeys();
    } catch (error: any) {
      console.error('Error deleting API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const toggleKeyStatus = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      toast.success(currentStatus ? 'API key disabled' : 'API key enabled');
      fetchApiKeys();
    } catch (error: any) {
      console.error('Error toggling API key:', error);
      toast.error('Failed to update API key');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('API key copied to clipboard!');
        setTimeout(() => setCopied(false), 3000);
        return;
      }
      
      // Fallback for older browsers or when clipboard API is not available
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        toast.success('API key copied to clipboard!');
        setTimeout(() => setCopied(false), 3000);
      } else {
        throw new Error('Copy command failed');
      }
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Failed to copy. Please select and copy manually.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyEndpoint = async (endpoint: string) => {
    const url = `${API_BASE_URL}${endpoint}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedEndpoint(endpoint);
      toast.success('Endpoint URL copied!');
      setTimeout(() => setCopiedEndpoint(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="space-y-6">
      {/* Generate New Key Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Generate API Key
          </CardTitle>
          <CardDescription>
            Create a new API key for Spotlight Wallet integration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="keyName" className="sr-only">Key Name</Label>
              <Input
                id="keyName"
                placeholder="Enter a descriptive name (e.g., Spotlight Wallet Production)"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && generateNewKey()}
              />
            </div>
            <Button onClick={generateNewKey} disabled={generating || !newKeyName.trim()}>
              {generating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Key
                </>
              )}
            </Button>
          </div>

          {/* Show Generated Key */}
          {generatedKey && (
            <Alert className="border-primary/30 bg-primary/5">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <AlertDescription className="ml-2">
                <p className="font-semibold text-primary mb-3">
                  Save this key now! It will not be shown again.
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 relative">
                    <Input
                      readOnly
                      value={showKey ? generatedKey : '•'.repeat(64)}
                      className="font-mono text-sm pr-24 bg-background"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => setShowKey(!showKey)}
                      >
                        {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => copyToClipboard(generatedKey)}
                      >
                        {copied ? (
                          <Check className="h-3.5 w-3.5 text-green-500" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={() => copyToClipboard(generatedKey)} 
                  className="w-full"
                  variant={copied ? "outline" : "default"}
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-2 text-green-500" />
                      Copied to Clipboard!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy API Key
                    </>
                  )}
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* API Documentation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Book className="h-5 w-5 text-primary" />
            API Documentation
          </CardTitle>
          <CardDescription>
            Learn how to use the API endpoints
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full">
                <Book className="h-4 w-4 mr-2" />
                View API Endpoints & Examples
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Spotlight Wallet API Reference
                </DialogTitle>
                <DialogDescription>
                  All endpoints require the <code className="bg-muted px-1 rounded">x-api-key</code> header
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="default">POST</Badge>
                      <h4 className="font-mono font-medium">/wallet-get-client</h4>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyEndpoint('/wallet-get-client')}
                    >
                      {copiedEndpoint === '/wallet-get-client' ? (
                        <><Check className="h-3.5 w-3.5 mr-1 text-green-500" /> Copied</>
                      ) : (
                        <><Copy className="h-3.5 w-3.5 mr-1" /> Copy URL</>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Fetch client data and loyalty points by email address
                  </p>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Example Request:</p>
                    <pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
{`curl -X POST \\
  ${API_BASE_URL}/wallet-get-client \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "client@example.com"}'`}
                    </pre>
                  </div>
                </div>

                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="default">POST</Badge>
                      <h4 className="font-mono font-medium">/wallet-purchase-webhook</h4>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyEndpoint('/wallet-purchase-webhook')}
                    >
                      {copiedEndpoint === '/wallet-purchase-webhook' ? (
                        <><Check className="h-3.5 w-3.5 mr-1 text-green-500" /> Copied</>
                      ) : (
                        <><Copy className="h-3.5 w-3.5 mr-1" /> Copy URL</>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Receive purchase notifications and automatically create service requests
                  </p>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Example Request:</p>
                    <pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
{`curl -X POST \\
  ${API_BASE_URL}/wallet-purchase-webhook \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "client@example.com",
    "service_name": "Social Media Management",
    "amount": 3000,
    "timestamp": "2025-01-01T00:00:00Z"
  }'`}
                    </pre>
                  </div>
                </div>

                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">GET</Badge>
                      <h4 className="font-mono font-medium">/wallet-get-services</h4>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyEndpoint('/wallet-get-services')}
                    >
                      {copiedEndpoint === '/wallet-get-services' ? (
                        <><Check className="h-3.5 w-3.5 mr-1 text-green-500" /> Copied</>
                      ) : (
                        <><Copy className="h-3.5 w-3.5 mr-1" /> Copy URL</>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Retrieve the complete service catalog with pricing
                  </p>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Example Request:</p>
                    <pre className="p-3 bg-muted rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
{`curl -X GET \\
  ${API_BASE_URL}/wallet-get-services \\
  -H "x-api-key: YOUR_API_KEY"`}
                    </pre>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Existing Keys Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            Your API Keys
          </CardTitle>
          <CardDescription>
            Manage your existing API keys
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading keys...</span>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>No API keys created yet</p>
              <p className="text-sm">Generate your first key above to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${key.is_active ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Key className={`h-4 w-4 ${key.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className="font-medium">{key.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {key.key_prefix}••••••••••••••••
                      </p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Full key only shown once at creation. Generate a new key if needed.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Created: {formatDate(key.created_at)}
                        {key.last_used_at && ` • Last used: ${formatDate(key.last_used_at)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={key.is_active ? "default" : "secondary"}>
                      {key.is_active ? "Active" : "Disabled"}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleKeyStatus(key.id, key.is_active)}
                    >
                      {key.is_active ? "Disable" : "Enable"}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => deleteKey(key.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
